<template>
  <div>
    <div class="card bg-primary text-white mb-4">
      <h5 class="card-header mb-0">{{ $t('Webportal.Actions.InquiryWizard.Step1.Title') }}</h5>
    </div>
    
    <div class="row mb-4">
      <!-- Left Column: Check Selection -->
      <div class="col-xl-6 col-lg-6 col-md-12 mb-3 mb-lg-0">
        <b-card class="h-100">
          <div class="form-group mb-0">
            <label for="check-select" class="col-form-label-sm">
              {{ $t('Webportal.Actions.InquiryWizard.Step1.SelectCheck') }}
              <span class="text-muted small">({{ $t('Webportal.Actions.InquiryWizard.DeadlineHint') }})</span>
            </label>
            <b-form-select
              id="check-select"
              v-model="selectedCheckId"
              :options="checkOptions"
              @change="handleCheckSelection"
              :disabled="isLoadingChecks"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>{{ isLoadingChecks ? $t('Webportal.Loading') : $t('Webportal.Actions.InquiryWizard.Step1.PleaseSelect') }}</b-form-select-option>
              </template>
            </b-form-select>
          </div>
        </b-card>
      </div>

      <!-- Right Column: Optimization Opportunities or Empty State -->
      <div class="col-xl-6 col-lg-6 col-md-12">
        <b-card class="optimization-container">
          <!-- Loading State -->
          <div v-if="isLoadingCheckData" class="d-flex align-items-center h-100">
            <div class="text-center w-100">
              <b-spinner variant="primary"></b-spinner>
              <p class="mt-2 text-muted">{{ $t('Webportal.Loading') }}...</p>
            </div>
          </div>

          <div v-else-if="!selectedCheckId || !checkData" class="d-flex align-items-center justify-content-center h-100">
            <!-- Empty State -->
            <div class="d-flex flex-column align-items-center justify-content-center">
              <font-awesome-icon :icon="['fal', 'exclamation-circle']" size="4x" class="text-muted mb-3" style="opacity: 0.3;" />
              <h6 class="text-muted font-weight-bold">{{ $t('Webportal.Actions.InquiryWizard.Step1.NoCheckSelected') }}</h6>
              <p class="text-muted text-center mb-0">
                {{ $t('Webportal.Actions.InquiryWizard.Step1.NoCheckSelectedHint') }}
              </p>
            </div>
          </div>

          <!-- Questions that can be complained -->
          <template v-else-if="complainableQuestions.length > 0">
            <div class="d-flex align-items-center mb-3">
              <font-awesome-icon :icon="['fal', 'exclamation-circle']" class="text-danger mr-2" />
              <h6 class="mb-0 font-weight-bold">{{ $t('Webportal.Actions.InquiryWizard.Step1.OptimizationTitle') }}</h6>
            </div>

            <div>
              <b-alert show variant="warning" class="mb-3 mr-2 small">
                <strong>{{ $t('Webportal.Actions.InquiryWizard.Step1.QuestionCount', { count: complainableQuestions.length }) }}</strong>
                {{ $t('Webportal.Actions.InquiryWizard.Step1.OptimizationInfo') }}
              </b-alert>

              <div class="questions-list">
                <div
                  v-for="(question, index) in complainableQuestions"
                  :key="question.SurveyQuestionId"
                  class="question-item p-3 mb-2 mr-2 border"
                >
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <div class="font-weight-bold">{{ question.QuestionNumber }}</div>
                    <b-badge v-if="question.PointsReached != null && question.PointsMax != null" variant="light" class="question-points p-2">
                      <span class="text-warning">{{ question.PointsReached }}</span> / {{ question.PointsMax }}
                    </b-badge>
                  </div>
                  <div class="small text-muted mb-1">{{ question.ClusterText }}</div>
                  <div>{{ question.QuestionText }}</div>
                </div>
              </div>
            </div>
          </template>

          <!-- No complainable questions -->
          <div v-else class="d-flex align-items-center h-100">
            <div class="text-center w-100">
              <font-awesome-icon :icon="['fal', 'check-circle']" size="3x" class="text-success mb-3" />
              <p class="text-muted">{{ $t('Webportal.Actions.InquiryWizard.Step1.NoOptimizations') }}</p>
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <!-- Process Info Box -->
    <b-card header-bg-variant="light" class="mb-4">
      <template #header>
        <h6 class="mb-0 font-weight-bold">{{ $t('Webportal.Actions.InquiryWizard.Step1.ProcessTitle') }}</h6>
      </template>
      <div>
        <div v-for="(step, index) in processSteps" :key="index" class="d-flex" :class="index > 0 ? 'mt-3' : ''">
          <div class="mr-3">
            <div class="step-rounded-circle d-flex align-items-center justify-content-center font-weight-bold"
            :class="step.isActive ? 'step-active' : ''">
              {{ step.number }}
            </div>
          </div>
          <div>
            <div class="font-weight-bold">{{ step.title }}</div>
            <div class="small text-muted">{{ step.description }}</div>
          </div>
        </div>
      </div>
    </b-card>
  
    <!-- Important Hints Box -->
    <b-card header-bg-variant="warning" class="mb-4">
      <template #header>
        <div class="d-flex align-items-center">
          <font-awesome-icon icon="layer-group" class="mr-2" />
          <h6 class="mb-0 font-weight-bold">{{ $t('Webportal.Actions.InquiryWizard.Step1.HintsTitle') }}</h6>
        </div>
      </template>
        <ul class="mb-0 pl-3">
        <li class="mb-2" v-if="surveyRevisionCreationLimitInDays">
          {{ $t('Webportal.Actions.InquiryWizard.Step1.HintDeadline', { days: surveyRevisionCreationLimitInDays }) }}
        </li>
        <li class="mb-2">
          {{ $t('Webportal.Actions.InquiryWizard.Step1.HintEvidence') }}
        </li>
      </ul>
    </b-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ActionService from '@/services/ActionService';

export default {
  name: 'Step1Identify',
  
  data() {
    return {
      selectedCheckId: null,
      availableChecks: [],
      isLoadingChecks: false,
      isLoadingCheckData: false,
      checkData: null,
    };
  },
  
  computed: {
    ...mapGetters('inquiryWizard', [
      'surveyRevisionCreationLimitInDays',
    ]),
    
    processSteps() {
      return [
        {
          number: 1,
          isActive: true,
          title: this.$t('Webportal.Actions.InquiryWizard.Step1.Process1Title'),
          description: this.$t('Webportal.Actions.InquiryWizard.Step1.Process1Description'),
        },
        {
          number: 2,
          title: this.$t('Webportal.Actions.InquiryWizard.Step1.Process2Title'),
          description: this.$t('Webportal.Actions.InquiryWizard.Step1.Process2Description'),
        },
        {
          number: 3,
          title: this.$t('Webportal.Actions.InquiryWizard.Step1.Process3Title'),
          description: this.$t('Webportal.Actions.InquiryWizard.Step1.Process3Description'),
        },
        {
          number: 4,
          title: this.$t('Webportal.Actions.InquiryWizard.Step1.Process4Title'),
          description: this.$t('Webportal.Actions.InquiryWizard.Step1.Process4Description'),
        },
      ];
    },

    checkOptions() {
      return this.availableChecks.map(check => ({
        value: check.SurveyInstanceId,
        text: check.RevisionMaxDate
          ? `${check.ExternalSurveyInstanceId} - ${check.ClientLocationName} (${this.$options.filters.msm_date(check.RevisionMaxDate)})`
          : `${check.ExternalSurveyInstanceId} - ${check.ClientLocationName}`,
      }));
    },

    complainableQuestions() {
      if (!this.checkData || !this.checkData.Questions) {
        return [];
      }
      return this.checkData.Questions.filter(q => q.IsComplainable);
    },
  },
  
  methods: {
    ...mapActions('inquiryWizard', [
      'selectCheck',
      'setSurveyRevisionCreationLimitInDays',
      'loadClientDefaultSurveyRevisionCreationLimitInDays',
      'nextStep',
      'previousStep',
    ]),

    async loadAvailableChecks() {
      this.isLoadingChecks = true;
      try {
        const response = await ActionService.getSurveyInstancesForInquiry({ onlyCanCreateSurveyRevision: true, onlyForComplaint: true });
        this.availableChecks = response.data || [];
      } catch (error) {
        console.error('Error loading available checks:', error);
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Step1.LoadChecksError'), {
          title: this.$t('Webportal.Error'),
          variant: 'danger'
        });
      } finally {
        this.isLoadingChecks = false;
      }
    },

    async handleCheckSelection() {
      this.selectCheck(this.selectedCheckId);
      
      if (!this.selectedCheckId) {
        this.checkData = null;
        return;
      }

      this.isLoadingCheckData = true;
      try {
        const response = await ActionService.getSurveyInstanceData(this.selectedCheckId);
        this.checkData = response.data;
        
        // Update SurveyRevisionCreationLimitInDays if returned (check-specific override)
        if (this.checkData?.SurveyInstance?.SurveyRevisionCreationLimitInDays) {
          this.setSurveyRevisionCreationLimitInDays(
            this.checkData.SurveyInstance.SurveyRevisionCreationLimitInDays
          );
        }
      } catch (error) {
        console.error('Error loading check data:', error);
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Step1.LoadCheckDataError'), {
          title: this.$t('Webportal.Error'),
          variant: 'danger'
        });
      } finally {
        this.isLoadingCheckData = false;
      }
    }
  },
  
  async created() {
    // Ensure the client default is available when the wizard was entered directly
    this.loadClientDefaultSurveyRevisionCreationLimitInDays();

    // Load available checks
    await this.loadAvailableChecks();
    
    // Restore selected check from store or preselect from route params (sid)
    const storedCheckId = this.$store.getters['inquiryWizard/selectedCheckId'];
    const routeSid = this.$route?.params?.sid ? parseInt(this.$route.params.sid, 10) : null;
    const initialCheckId = routeSid || storedCheckId;
    if (initialCheckId && this.availableChecks.some(c => c.SurveyInstanceId === initialCheckId)) {
      // scroll to top to ensure the user sees the loading state of the wizard
      window.scrollTo(0, 0);
      this.selectedCheckId = initialCheckId;
      this.handleCheckSelection();
    }
  },
};
</script>

<style lang="scss" scoped>
.optimization-container {
  height: 570px
}

.questions-list {
  height: 400px;
  overflow-y: auto;
}

.question-item {
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(4px);
  }

  .question-points {
    font-size: 0.9rem;
    font-weight: normal;
  }
}

.step-rounded-circle {
  width: 32px;
  height: 32px;
  border-radius: 50% !important;
  background-color: lightgray;

  &.step-active {
    background-color: var(--primary);
    color: var(--white);
  }
}
</style>
